'use client';

import { GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { withSpmCnt } from '@/reporting/spm';
import { EPlatform } from '@/utils/enums/platform-enum';
import { checkFooterConfig } from '@/utils/tools';
import { getSpmPositionCode, useGlobalSpmTerminal } from '@/components/Layout/spmTerminal';
import { usePathname } from 'next/navigation';
import { FC, useEffect, useMemo } from 'react';
import { getFooterSocialPositionMap } from './socialSpmPosition';
import FooterMobile from './variants/2026-wap/Footer.m';
import FooterPC from './variants/2026-pc/Footer';

interface FooterProps {
  navList: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
  deviceType: EPlatform;
}

const Footer: FC<FooterProps> = ({ navList, deviceType }) => {
  const { trackEvent } = useReporting();
  const setIsShowFooter = useStore((state) => state.setIsShowFooterNav);
  const setIsShowFooterSubscribe = useStore((state) => state.setIsShowFooterSubscribe);
  const path = usePathname();
  const isMobile = deviceType !== EPlatform.WEB;
  const { cnName } = useRouteMetadata();
  // 全局 Footer 的 B 位固定为全局终端（global_pc/global_wap），与所在页面无关。
  const spmPage = useGlobalSpmTerminal(deviceType);

  const footerConfig = useMemo(() => checkFooterConfig(path, isMobile), [path, isMobile]);

  useEffect(() => {
    setIsShowFooter(footerConfig.isShowFooterNav);
    setIsShowFooterSubscribe(footerConfig.isShowFooterSubscribe);
  }, [footerConfig, setIsShowFooter, setIsShowFooterSubscribe]);

  const SDSocialMediaClick = (btn: string) => {
    const socialPosition = getFooterSocialPositionMap(spmPage)[btn];
    const spmCnt = socialPosition
      ? getSpmPositionCode(spmPage, 'footer_social', socialPosition)
      : undefined;
    const properties = {
      btn_name: btn,
      page_name: cnName,
      url_path: window.location.href,
    };
    trackEvent(
      {
        eventName: 'Socialmedia_Click',
        properties: spmCnt ? withSpmCnt(properties, spmCnt) : properties,
      },
      [ProviderType.SENSORS],
    );
  };

  const SDBottomNavTabClick = (
    first_tab_name: string,
    second_tab_name?: string,
    first_tab_index?: number,
    second_tab_index?: number,
    spmCnt?: string,
  ) => {
    const properties = {
      first_tab_name,
      second_tab_name,
      first_tab_index,
      second_tab_index,
      page_name: cnName,
      url_path: window.location.href,
    };
    trackEvent(
      {
        eventName: 'BottomNavTab_Click',
        properties: spmCnt ? withSpmCnt(properties, spmCnt) : properties,
      },
      [ProviderType.SENSORS],
    );
  };

  return footerConfig.isShowFooterNav ? (
    <>
      <WebOnly>
        <FooterPC
          navList={navList}
          SDSocialMediaClick={SDSocialMediaClick}
          SDBottomNavTabClick={SDBottomNavTabClick}
          isShowSubscribe={footerConfig.isShowFooterSubscribe}
          deviceType={deviceType}
        />
      </WebOnly>
      <WapOnly hideApp>
        <FooterMobile
          navList={navList}
          SDSocialMediaClick={SDSocialMediaClick}
          SDBottomNavTabClick={SDBottomNavTabClick}
          isShowSubscribe={footerConfig.isShowFooterSubscribe}
          deviceType={deviceType}
        />
      </WapOnly>
    </>
  ) : null;
};

export default Footer;
